import { supabase } from '../lib/supabaseClient'
import { X, Plus, Trash2 } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { isPlatformStaff } from '../lib/roleHelpers'

const DEFAULT_COLORS = [
  '#f59e0b', '#10b981', '#3b82f6', '#ef4444',
  '#8b5cf6', '#06b6d4', '#f97316', '#ec4899',
]

const C = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' },
  modal: { background: 'linear-gradient(135deg, #001408, #00200c)', border: '1px solid rgba(0,255,102,.2)', width: '100%', maxWidth: '448px', padding: '24px', maxHeight: '90vh', overflowY: 'auto' },
  header: { fontFamily: "'VT323', monospace", fontSize: '20px', color: '#00ff66', textShadow: '0 0 6px rgba(0,255,102,.4)', letterSpacing: '0.08em', textTransform: 'uppercase' },
  label: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: '#3a5c3a', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' },
  input: { width: '100%', background: 'rgba(0,20,8,.8)', color: '#00ff66', border: '1px solid rgba(0,255,102,.25)', padding: '10px 14px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px', outline: 'none' },
  select: { width: '100%', background: 'rgba(0,20,8,.8)', color: '#00ff66', border: '1px solid rgba(0,255,102,.25)', padding: '10px 14px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px', outline: 'none', appearance: 'none' },
  btnPrimary: { width: '100%', background: '#00ff66', color: '#000', border: 'none', padding: '14px 20px', fontFamily: "'VT323', monospace", fontSize: '18px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', textShadow: 'none' },
  btnOption: (active) => ({
    textAlign: 'left', padding: '8px 12px', cursor: 'pointer', border: active ? '1px solid #00ff66' : '1px solid rgba(0,255,102,.15)',
    background: active ? 'rgba(0,255,102,.12)' : 'rgba(0,20,8,.4)', transition: 'all 0.15s',
  }),
  btnOptionLabel: (active) => ({ fontFamily: "'VT323', monospace", fontSize: '14px', color: active ? '#00ff66' : '#3a5c3a', letterSpacing: '0.06em' }),
  btnOptionDesc: (active) => ({ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', color: active ? 'rgba(0,255,102,.6)' : '#1a3a1a', marginTop: '2px' }),
  link: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: '#00ff66', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textShadow: '0 0 4px rgba(0,255,102,.3)' },
  error: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: '#ff4136', marginTop: '6px' },
  hint: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', color: '#1a3a1a', marginTop: '6px' },
  // Track 6.2 — 44x44 min hit area on the modal close.
  close: { background: 'none', border: 'none', cursor: 'pointer', color: '#3a5c3a', padding: 8, minHeight: 44, minWidth: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  factionInput: { flex: 1, background: 'rgba(0,20,8,.8)', color: '#00ff66', border: '1px solid rgba(0,255,102,.25)', padding: '8px 12px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', outline: 'none' },
  trashBtn: { background: 'none', border: 'none', color: '#3a5c3a', cursor: 'pointer', padding: '4px', transition: 'color 0.15s' },
}

const generateJoinCode = () => {
  const chars = 'ABCDEFGHJKLMNQRSTUVWXYZ23456789'
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
}

export default function CreateEvent({ user, profile, onClose, onCreated }) {
  const [name, setName] = useState('')
  const [orgId, setOrgId] = useState('')
  const [orgs, setOrgs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showCreateOrg, setShowCreateOrg] = useState(false)
  const [orgName, setOrgName] = useState('')
  const [registrationMode, setRegistrationMode] = useState('invite')
  const [mcsBuildMode, setMcsBuildMode] = useState('gm_led')
  const [factions, setFactions] = useState([
    { name: '', color: DEFAULT_COLORS[0] },
    { name: '', color: DEFAULT_COLORS[1] },
  ])
  const submitting = useRef(false)

  useEffect(() => { fetchOrgs() }, [])

  const fetchOrgs = async () => {
    // ANU-2: m55_gm and similar non-platform users were seeing every org
    // because organizations.orgs_select RLS allows any authenticated read.
    // Platform staff still need the full list (they create orgs on behalf
    // of customers); everyone else gets scoped to orgs they actually
    // belong to via org_members with a manager-tier role.
    if (isPlatformStaff(profile)) {
      const { data } = await supabase.from('organizations').select('*').order('name')
      setOrgs(data || [])
      if ((data?.length ?? 0) === 0) setShowCreateOrg(true)
      return
    }
    const { data } = await supabase
      .from('org_members')
      .select('organizations(*)')
      .eq('user_id', user.id)
      .in('org_role', ['org_owner', 'org_admin', 'org_gm'])
    const seen = new Set()
    const orgsList = (data || [])
      .map(r => r.organizations)
      .filter(o => o && !seen.has(o.id) && seen.add(o.id))
    orgsList.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    setOrgs(orgsList)
  }

  const handleCreateOrg = async () => {
    if (!orgName.trim()) return
    // Bug 39: this flow is only exposed to platform staff. Staff are not
    // the org owner — they spin orgs up on behalf of customers. owner_id
    // stays NULL until designated in /admin; staff gets an org_admin
    // member row so they can keep managing the org.
    const { data: created, error } = await supabase
      .from('organizations')
      .insert({ name: orgName, slug: orgName.toLowerCase().replace(/\s+/g, '-'), owner_id: null })
      .select('id, name, slug')
    if (error) return
    const newOrgId = created?.[0]?.id
    if (newOrgId) {
      await supabase.from('org_members').insert({ org_id: newOrgId, user_id: user.id, org_role: 'org_admin' })
    }
    setOrgs([created]); setOrgId(newOrgId); setShowCreateOrg(false)
  }

  const addFaction = () => {
    if (factions.length >= 8) return
    const usedColors = factions.map(f => f.color)
    const nextColor = DEFAULT_COLORS.find(c => !usedColors.includes(c)) || DEFAULT_COLORS[factions.length % DEFAULT_COLORS.length]
    setFactions([...factions, { name: '', color: nextColor }])
  }

  const removeFaction = (index) => {
    if (factions.length <= 2) return
    setFactions(factions.filter((_, i) => i !== index))
  }

  const updateFaction = (index, field, value) => {
    setFactions(factions.map((f, i) => i === index ? { ...f, [field]: value } : f))
  }

  const handleCreate = async () => {
    if (loading) return
    if (!name.trim()) return setError('Event name is required')
    if (factions.some(f => !f.name.trim())) return setError('All faction names are required')

    setLoading(true)
    setError('')

    try {
      const joinCode = generateJoinCode()
      const { data, error } = await supabase.rpc('create_event_with_factions', {
        p_org_id: orgId || null,
        p_org_name: orgId ? null : orgName,
        p_event_name: name.trim(),
        p_join_code: joinCode,
        p_reg_mode: registrationMode,
        p_mcs_mode: mcsBuildMode,
        p_factions: factions.filter(f => f.name.trim()),
      })

      if (error) { setError(error.message); return }
      onCreated?.(data)
    } catch (err) {
      setError('Unexpected error: ' + err.message)
    } finally {
      setLoading(false)
      submitting.current = false
    }
  }

  return (
    <div style={C.overlay}>
      <div style={C.modal} className="modal-scroll">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={C.header}>{'>'}_ New Event</h2>
          <button onClick={onClose} style={C.close}><X size={20} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {showCreateOrg ? (
            <div>
              <label style={C.label}>Create Organization First</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input type="text" value={orgName} onChange={(e) => setOrgName(e.target.value)}
                  placeholder="e.g. P3 Productions" maxLength={50} style={C.input} />
                <button onClick={handleCreateOrg} style={{ ...C.btnPrimary, width: 'auto', padding: '10px 16px', fontSize: '14px' }}>Create</button>
              </div>
            </div>
          ) : (
            <div>
              <label style={C.label}>Organization</label>
              <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={C.select}>
                <option value="">Select organization</option>
                {orgs.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
              </select>
              {error && <p style={C.error}>{error}</p>}
              {isPlatformStaff(profile) && (
                <button onClick={() => setShowCreateOrg(true)} style={{ ...C.link, marginTop: '6px' }}>
                  + Create new organization
                </button>
              )}
              {!isPlatformStaff(profile) && orgs.length === 0 && (
                <p style={C.hint}>No organizations available. Contact your M55 admin.</p>
              )}
            </div>
          )}

          <div>
            <label style={C.label}>Event Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Operation Night Hawk" maxLength={60} style={C.input} />
          </div>

          <div>
            <label style={C.label}>Registration</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {[
                { value: 'invite', label: 'Invite', desc: 'QR / link only' },
                { value: 'open', label: 'Open', desc: 'Anyone can join' },
                { value: 'manual', label: 'Manual', desc: 'GM adds players' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setRegistrationMode(opt.value)} style={C.btnOption(registrationMode === opt.value)}>
                  <div style={C.btnOptionLabel(registrationMode === opt.value)}>{opt.label}</div>
                  <div style={C.btnOptionDesc(registrationMode === opt.value)}>{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={C.label}>Command Structure</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {[
                { value: 'gm_led', label: 'GM Led', desc: 'GM builds full MCS' },
                { value: 'commander_led', label: 'Commander Led', desc: 'Commanders build down' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setMcsBuildMode(opt.value)} style={C.btnOption(mcsBuildMode === opt.value)}>
                  <div style={C.btnOptionLabel(mcsBuildMode === opt.value)}>{opt.label}</div>
                  <div style={C.btnOptionDesc(mcsBuildMode === opt.value)}>{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ ...C.label, marginBottom: 0 }}>Factions</label>
              <button onClick={addFaction} disabled={factions.length >= 8}
                style={{ ...C.link, opacity: factions.length >= 8 ? 0.4 : 1, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Plus size={12} /> Add faction
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {factions.map((faction, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="color" value={faction.color} onChange={(e) => updateFaction(index, 'color', e.target.value)}
                    style={{ width: '36px', height: '36px', cursor: 'pointer', background: 'transparent', border: 0, padding: '2px' }} />
                  <input type="text" value={faction.name} onChange={(e) => updateFaction(index, 'name', e.target.value)}
                    placeholder={`Faction ${index + 1} name (e.g. OPFOR, Tan, Wolf)`} maxLength={30}
                    style={C.factionInput} />
                  <button onClick={() => removeFaction(index)} disabled={factions.length <= 2}
                    style={{ ...C.trashBtn, opacity: factions.length <= 2 ? 0.3 : 1 }}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
            <p style={C.hint}>Factions without names will be skipped</p>
          </div>

          <button onClick={handleCreate} disabled={loading || !name.trim() || !orgId}
            style={{ ...C.btnPrimary, opacity: (loading || !name.trim() || !orgId) ? 0.5 : 1 }}>
            {loading ? 'Creating...' : 'Create Event'}
          </button>

        </div>
      </div>
    </div>
  )
}
